import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tags, Type, Plus, X, FileSpreadsheet, Link2, CheckCircle2, Unplug, ShieldCheck, ScrollText, Settings } from "lucide-react";
import { PageHeader } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import StaffManager from "@/components/admin/StaffManager";
import { AuditLog, OtpLog } from "@/components/admin/AuditLog";
import SyncBackupManager from "@/components/admin/SyncBackupManager";
import MonthlyRecapManager from "@/components/admin/MonthlyRecapManager";

export default function Pengaturan() {
  return (
    <div className="space-y-6">
      <PageHeader icon={Settings} title="Pengaturan" subtitle="Khusus Super Admin: akun & peran pengurus, gateway OTP, Google Sheet, pilihan dropdown, tampilan awal, dan audit log." />
      <Tabs defaultValue="akun">
        <TabsList className="glass rounded-2xl p-1.5 h-auto flex flex-wrap gap-1 justify-start" data-testid="pengaturan-tabs">
          {[["akun", ShieldCheck, "Akun & Admin"], ["sinkron", FileSpreadsheet, "Sinkronisasi & Backup"], ["kategori", Tags, "Kategori & Tampilan"], ["audit", ScrollText, "Audit Log"]].map(([v, I, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`pengaturan-tab-${v}`} className="rounded-xl gap-2 px-4 py-2.5 data-[state=active]:bg-emerald-700 data-[state=active]:text-white data-[state=active]:shadow"><I className="h-4 w-4" /> {l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="akun" className="space-y-6 pt-4"><StaffManager /><MonthlyRecapManager /><OtpLog /></TabsContent>
        <TabsContent value="sinkron" className="space-y-6 pt-4"><GoogleSheetsManager /></TabsContent>
        <TabsContent value="kategori" className="space-y-6 pt-4"><OptionsManager /><MotivasiManager /><SiteManager /></TabsContent>
        <TabsContent value="audit" className="space-y-6 pt-4"><AuditLog /></TabsContent>
      </Tabs>
    </div>
  );
}

const OPT_LABELS = { departemen: "Departemen", ribath: "Ribath", smt: "SMT", prodi_ket: "Prodi / Ket." };

function GoogleSheetsManager() {
  const qc = useQueryClient();
  const { data: status } = useQuery({ queryKey: ["sheets-status"], queryFn: async () => (await api.get("/sheets/status")).data });
  const [url, setUrl] = useState("");
  const [depUrl, setDepUrl] = useState("");

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("sheets");
    if (p === "connected") { toast.success("Akun Google terhubung"); qc.invalidateQueries({ queryKey: ["sheets-status"] }); }
    else if (p === "error") toast.error("Gagal menghubungkan Google");
    if (p) window.history.replaceState({}, "", window.location.pathname);
  }, [qc]);

  const cfgMut = useMutation({
    mutationFn: async () => api.post("/sheets/config", { sheet_url: url }),
    onSuccess: (r) => { toast.success(`Terhubung ke tab "${r.data.tab_title}"`); setUrl(""); qc.invalidateQueries({ queryKey: ["sheets-status"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Gagal"),
  });
  const depCfgMut = useMutation({
    mutationFn: async () => api.post("/sheets/config", { sheet_url: `https://docs.google.com/spreadsheets/d/${status.spreadsheet_id}/edit#gid=${status.gid ?? 0}`, departemen_url: depUrl }),
    onSuccess: (r) => { toast.success(`Tab Departemen aktif: "${r.data.departemen_tab}"`); setDepUrl(""); qc.invalidateQueries({ queryKey: ["sheets-status"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Gagal mengatur tab departemen"),
  });
  const disMut = useMutation({
    mutationFn: async () => api.post("/sheets/disconnect"),
    onSuccess: () => { toast.success("Google diputus"); qc.invalidateQueries({ queryKey: ["sheets-status"] }); },
  });
  const connect = () => { window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/oauth/sheets/login`; };

  return (
    <>
      <div className="glass rounded-2xl p-6 space-y-4" data-testid="google-sheets-manager">
        <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300"><FileSpreadsheet className="h-5 w-5" /><span className="font-bold text-lg">Koneksi Google Sheet</span></div>
        {status && !status.configured && (
          <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-3" data-testid="sheets-not-configured">Fitur ini butuh kredensial Google (Client ID & Secret) di server.</div>
        )}
        {status?.configured && (
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-white/50 dark:bg-white/5 rounded-xl p-3 flex-wrap gap-2">
              <div className="text-sm">
                {status.connected ? <span className="inline-flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-medium" data-testid="sheets-connected"><CheckCircle2 className="h-4 w-4" /> Terhubung{status.email ? ` sebagai ${status.email}` : ""}</span>
                  : <span className="text-slate-500 dark:text-slate-300">Belum terhubung ke akun Google</span>}
              </div>
              {status.connected ? (
                <Button variant="ghost" size="sm" className="gap-2 text-slate-500 dark:text-slate-300 hover:text-rose-600" data-testid="sheets-disconnect-button" onClick={() => { if (window.confirm("Putuskan koneksi akun Google? Impor & ekspor backup ke Sheet tidak bisa dilakukan sampai dihubungkan kembali.")) disMut.mutate(); }}><Unplug className="h-4 w-4" /> Putuskan</Button>
              ) : (
                <Button size="sm" className="gap-2 bg-emerald-700 hover:bg-emerald-800" data-testid="sheets-connect-button" onClick={connect}><Link2 className="h-4 w-4" /> Hubungkan Google</Button>
              )}
            </div>
            {status.connected && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs">Link Google Sheet rekap (tab Ribath)</Label>
                  <div className="flex gap-2">
                    <Input placeholder="https://docs.google.com/spreadsheets/d/..." value={url} onChange={(e) => setUrl(e.target.value)} data-testid="sheets-url-input" />
                    <Button className="shrink-0 bg-emerald-700 hover:bg-emerald-800" data-testid="sheets-save-config" disabled={!url || cfgMut.isPending} onClick={() => cfgMut.mutate()}>Simpan</Button>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Input placeholder="Link tab REKAP DEPARTEMEN (…#gid=…)" value={depUrl} onChange={(e) => setDepUrl(e.target.value)} data-testid="departemen-url-input" className="flex-1 min-w-[220px]" />
                  <Button variant="secondary" className="shrink-0" data-testid="departemen-save-config" disabled={!depUrl || depCfgMut.isPending} onClick={() => depCfgMut.mutate()}>Atur Tab Departemen</Button>
                </div>
                <details className="text-xs text-slate-500 dark:text-slate-300" data-testid="sheets-details">
                  <summary className="cursor-pointer hover:text-emerald-700 dark:text-emerald-400">Lihat detail tab & waktu impor terakhir</summary>
                  <ul className="mt-2 space-y-1 pl-4 list-disc">
                    <li>Ribath: <strong>{status.tab_title || "-"}</strong>{status.last_pull && ` · impor ${new Date(status.last_pull).toLocaleString("id-ID")}`}</li>
                    <li>Kuliah: <strong>{status.kuliah_tab || "-"}</strong>{status.last_pull_kuliah && ` · impor ${new Date(status.last_pull_kuliah).toLocaleString("id-ID")}`}</li>
                    <li>Ngaji Dhuha: <strong>{status.ngaji_tab || "-"}</strong>{status.last_pull_ngaji && ` · impor ${new Date(status.last_pull_ngaji).toLocaleString("id-ID")}`}</li>
                    <li>Departemen: <strong>{status.departemen_tab || "-"}</strong>{status.last_pull_departemen && ` · impor ${new Date(status.last_pull_departemen).toLocaleString("id-ID")}`}</li>
                  </ul>
                </details>
              </>
            )}
          </div>
        )}
      </div>
      <SyncBackupManager status={status} />
    </>
  );
}

function MotivasiManager() {
  const qc = useQueryClient();
  const { data: mot } = useQuery({ queryKey: ["motivasi"], queryFn: async () => (await api.get("/motivasi")).data });
  const [form, setForm] = useState(null);
  useEffect(() => { if (mot && !form) setForm({ a: mot.a || "", b: mot.b || "", c: mot.c || "", d: mot.d || "" }); }, [mot, form]);
  const mut = useMutation({
    mutationFn: async () => api.put("/motivasi", form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["motivasi"] }); toast.success("Catatan motivasi disimpan"); },
  });
  if (!form) return null;
  const BRACKETS = [
    { k: "a", label: "80 - 100% (Sangat Baik)" },
    { k: "b", label: "60 - 79% (Baik)" },
    { k: "c", label: "40 - 59% (Cukup)" },
    { k: "d", label: "0 - 39% (Perlu Perhatian)" },
  ];
  return (
    <div className="glass rounded-2xl p-6 space-y-4" data-testid="motivasi-manager">
      <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300"><Type className="h-5 w-5" /><span className="font-bold text-lg">Catatan Motivasi (berdasarkan rata-rata kehadiran)</span></div>
      <div className="grid sm:grid-cols-2 gap-3">
        {BRACKETS.map((b) => (
          <div key={b.k} className="space-y-1.5">
            <Label className="text-xs">{b.label}</Label>
            <Textarea data-testid={`motivasi-${b.k}`} value={form[b.k]} placeholder="Tulis kata-kata motivasi..." onChange={(e) => setForm({ ...form, [b.k]: e.target.value })} />
          </div>
        ))}
      </div>
      <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-motivasi-button" onClick={() => mut.mutate()} disabled={mut.isPending}>Simpan Catatan</Button>
    </div>
  );
}

function OptionsManager() {
  const qc = useQueryClient();
  const { data: options = {} } = useQuery({ queryKey: ["options"], queryFn: async () => (await api.get("/options")).data });
  const [local, setLocal] = useState(null);
  const [inputs, setInputs] = useState({});
  useEffect(() => { if (options && !local) setLocal(options); }, [options, local]);

  const saveMut = useMutation({
    mutationFn: async (val) => api.put("/options", val),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["options"] }); toast.success("Pilihan disimpan"); },
  });

  if (!local) return null;
  const addOpt = (k) => {
    const v = (inputs[k] || "").trim();
    if (!v) return;
    const next = { ...local, [k]: [...(local[k] || []), v] };
    setLocal(next); setInputs({ ...inputs, [k]: "" }); saveMut.mutate(next);
  };
  const rmOpt = (k, i) => { const next = { ...local, [k]: local[k].filter((_, idx) => idx !== i) }; setLocal(next); saveMut.mutate(next); };

  return (
    <div className="glass rounded-2xl p-6 space-y-4">
      <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300"><Tags className="h-5 w-5" /><span className="font-bold text-lg">Pilihan Dropdown Data Santri</span></div>
      <div className="grid sm:grid-cols-2 gap-4">
        {Object.keys(OPT_LABELS).map((k) => (
          <div key={k} className="space-y-2" data-testid={`option-group-${k}`}>
            <Label className="text-xs">{OPT_LABELS[k]}</Label>
            <div className="flex flex-wrap gap-1.5">
              {(local[k] || []).map((v, i) => (
                <Badge key={i} className="bg-emerald-100 text-emerald-700 border-0 gap-1 dark:bg-emerald-500/20 dark:text-emerald-200 dark:border dark:border-emerald-500/30">{v}<button onClick={() => rmOpt(k, i)} data-testid={`rm-${k}-${i}`}><X className="h-3 w-3" /></button></Badge>
              ))}
            </div>
            <div className="flex gap-1.5">
              <Input className="h-8 text-sm" placeholder={`Tambah ${OPT_LABELS[k]}`} value={inputs[k] || ""} data-testid={`add-input-${k}`}
                onChange={(e) => setInputs({ ...inputs, [k]: e.target.value })} onKeyDown={(e) => e.key === "Enter" && addOpt(k)} />
              <Button size="icon" variant="outline" className="h-8 w-8 shrink-0" data-testid={`add-opt-${k}`} onClick={() => addOpt(k)}><Plus className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SiteManager() {
  const qc = useQueryClient();
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: async () => (await api.get("/site-settings")).data });
  const [form, setForm] = useState(null);
  useEffect(() => { if (site && !form) setForm(site); }, [site, form]);

  const mut = useMutation({
    mutationFn: async () => api.put("/site-settings", form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["site"] }); toast.success("Tampilan awal diperbarui"); },
  });
  if (!form) return null;

  return (
    <div className="glass rounded-2xl p-6 space-y-4">
      <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300"><Type className="h-5 w-5" /><span className="font-bold text-lg">Teks Tampilan Awal (Halaman Login)</span></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Nama Aplikasi</Label><Input value={form.app_name} data-testid="site-app-name" onChange={(e) => setForm({ ...form, app_name: e.target.value })} /></div>
        <div className="space-y-1.5"><Label className="text-xs">Sub Nama</Label><Input value={form.app_sub} data-testid="site-app-sub" onChange={(e) => setForm({ ...form, app_sub: e.target.value })} /></div>
      </div>
      <div className="space-y-1.5"><Label className="text-xs">Judul Utama</Label><Input value={form.hero_title} data-testid="site-hero-title" onChange={(e) => setForm({ ...form, hero_title: e.target.value })} /></div>
      <div className="space-y-1.5"><Label className="text-xs">Deskripsi</Label><Textarea value={form.hero_subtitle} data-testid="site-hero-subtitle" onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })} /></div>
      <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-site-button" onClick={() => mut.mutate()} disabled={mut.isPending}>Simpan Tampilan</Button>
    </div>
  );
}
